import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { ICellRendererParams } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Button, EllipsisText, Flex } from 'components/ui';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { globalTheme } from 'styles/global-theme';
import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { formatAUD } from 'lib/helpers';
import {
  BookingConfig,
  BookingStockSearchResult,
} from '../../lib/api/generated';

interface StockBookRequiredMobileProps {
  config: BookingConfig;
  stockData: BookingStockSearchResult[] | undefined;
  dataLoading: boolean;
  productType?: string;
  category?: string[];
}

interface StockGroupRow {
  group: string;
  name: string;
  kill: number;
  required: number;
  incoming: number;
  [id: string]: any;
}

const ColorButton = styled(Button)(() => ({
  color: 'white',
  backgroundColor: '#C934E1',
}));

const ValueWrapper = styled(Flex)`
  padding: 4px 0;
`;

export const StockBookRequiredMobile = ({
  stockData,
  dataLoading,
  productType,
  config,
  category,
}: StockBookRequiredMobileProps) => {
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const [gridHeight, setGridHeight] = useState<number>(0);
  const gridRef = useRef<any>();
  const router = useRouter();
  const datesRef = useRef<Record<string, Record<string, string>>>({});

  const loadingTemplate = `<span class="ag-overlay-loading-center" style="padding: 10px;  background: #55AA77; color: #ffffff;">Loading...</span>`;

  const numberFormatter = useCallback((params: any) => {
    return params.value?.toLocaleString();
  }, []);

  const calculateGridHeight = useCallback(() => {
    const windowHeight = window.innerHeight;
    const navbarHeight = 190;
    const buttonHeight = 64;
    const calculatedHeight = windowHeight - navbarHeight - buttonHeight;
    setGridHeight(calculatedHeight);
  }, []);

  useEffect(() => {
    calculateGridHeight();
    window.addEventListener('resize', calculateGridHeight);
    return () => {
      window.removeEventListener('resize', calculateGridHeight);
    };
  }, [calculateGridHeight]);

  const getFilteredCategories = useCallback(() => {
    if (!productType) return { groupRows: {} };
    const currentCategory = config.categories?.[productType];
    if (!currentCategory) return { groupRows: {} };

    const groupRows: { [id: string]: string } = {};

    const validCategories = currentCategory.values.filter((cat: any) => {
      if (!category || !category.length) return true;
      return category.includes(cat.key);
    });

    validCategories.forEach((cat: any) => {
      groupRows[`m_${cat.key}`] = cat.name;
    });

    return { groupRows };
  }, [config.categories, productType, category]);

  const processData = useCallback(
    (incomingData: any) => {
      if (!stockData) return undefined;

      const emptyData: { [id: string]: StockGroupRow } = {};
      const groups = new Set<string>();
      const { groupRows } = getFilteredCategories();

      datesRef.current = {};

      incomingData.forEach((obj: any) => {
        groups.add(obj.group);
        datesRef.current[obj.group] = {
          startDate: config?.settings?.monthly
            ? format(startOfMonth(new Date(obj.date)), 'yyyy-MM-dd')
            : format(startOfWeek(new Date(obj.date)), 'yyyy-MM-dd'),
          endDate: config?.settings?.monthly
            ? format(endOfMonth(new Date(obj.date)), 'yyyy-MM-dd')
            : format(endOfWeek(new Date(obj.date)), 'yyyy-MM-dd'),
        };
      });

      groups.forEach((groupName) => {
        Object.keys(groupRows).forEach((rowId) => {
          const dataKey = `${groupName}_${rowId}`;
          emptyData[dataKey] = {
            group: groupName,
            name: groupRows[rowId],
            incoming: 0,
            required: 0,
            kill: 0,
            dates: datesRef.current[groupName],
          };
        });
      });

      stockData.forEach((groupData: any) => {
        const groupName = groupData.group;
        Object.entries(groupData).forEach(([key, value]) => {
          const match = key.match(/^(incoming|kill)_(m_.*)$/);
          if (match) {
            const type = match[1];
            const rowId = match[2];
            const dataKey = `${groupName}_${rowId}`;
            if (emptyData[dataKey]) {
              emptyData[dataKey][type] +=
                typeof value !== 'string' ? (value as number) : 0;
            }
          }
        });
      });

      return Object.values(emptyData).map((row) => ({
        ...row,
        required: row.kill - row.incoming,
      }));
    },
    [stockData, config?.settings?.monthly, getFilteredCategories]
  );

  const handleClickSummary = (
    startDate: string,
    endDate: string,
    type: string
  ) => {
    router.push(`/stockbook?type=${type}&date=${startDate}&date_to=${endDate}`);
  };

  const purchasedCellRenderer = useCallback(
    (props: any) => {
      if (props.node.parent?.id === 'ROOT_NODE_ID') {
        return (
          <ColorButton
            size='xSmall'
            color='pink'
            onClick={() => {
              handleClickSummary?.(
                datesRef.current[props.node.key]?.startDate,
                datesRef.current[props.node.key]?.endDate,
                productType as string
              );
            }}
            fontSize={4}
          >
            View
          </ColorButton>
        );
      }
      return valueRenderer(props);
    },
    [productType]
  );

  const valueRenderer = (params: any) => {
    return (
      <ValueWrapper padding={0}>
        <EllipsisText fontSize={4} color={params.value < 0 ? 'red' : 'black'}>
          {typeof params.value === 'number'
            ? formatAUD(params.value, false, false)
            : params.value}
        </EllipsisText>
      </ValueWrapper>
    );
  };

  const columnDefs: any = useMemo(() => {
    return [
      {
        field: 'group',
        rowGroup: true,
        hide: true,
        suppressMenu: true,
        suppressSorting: true,
      },
      {
        field: 'name',
        rowGroup: true,
        hide: true,
        headerClass: 'stockbook-required-header',
        cellRenderer: valueRenderer,
        suppressMenu: true,
      },
      {
        headerName: 'Req',
        headerClass: 'stockbook-required-header',
        field: 'required',
        valueFormatter: numberFormatter,
        cellRenderer: valueRenderer,
        aggFunc: 'sum',
        suppressMenu: true,
        suppressSorting: true,
        minWidth: 80,
        width: 100,
        maxWidth: 250,
      },
      {
        headerName: 'Tgt',
        headerClass: 'stockbook-required-header',
        field: 'kill',
        valueFormatter: numberFormatter,
        cellRenderer: valueRenderer,
        aggFunc: 'sum',
        suppressMenu: true,
        suppressSorting: true,
        minWidth: 80,
        width: 100,
        maxWidth: 250,
      },
      {
        headerName: 'Pur',
        headerClass: 'stockbook-required-header',
        field: 'incoming',
        valueFormatter: numberFormatter,
        aggFunc: 'sum',
        cellRenderer: purchasedCellRenderer,
        suppressMenu: true,
        suppressSorting: true,
        minWidth: 100,
        width: 100,
        maxWidth: 250,
      },
    ];
  }, [numberFormatter, purchasedCellRenderer]);

  useEffect(() => {
    if (gridRef?.current?.api) {
      dataLoading
        ? gridRef.current.api.showLoadingOverlay()
        : gridRef.current.api.hideOverlay();
    }
  }, [dataLoading]);

  return (
    <div style={{ height: gridHeight }}>
      <div style={gridStyle} className={'ag-theme-quartz'}>
        <AgGridReact
          ref={gridRef}
          rowData={processData(stockData)}
          columnDefs={columnDefs}
          defaultColDef={{
            flex: 1,
            minWidth: 80,
            cellRenderer: valueRenderer,
          }}
          groupDisplayType={'singleColumn'}
          autoGroupColumnDef={{
            headerName: config?.settings?.monthly ? 'Month' : 'Weekly',
            flex: 1,
            minWidth: 120,
            maxWidth: 250,
            headerClass: 'stockbook-required-header',
            cellRenderer: valueRenderer,
            cellRendererParams: {
              suppressCount: true,
            },
            cellRendererSelector: (params: ICellRendererParams) => {
              if (params.node.parent?.id !== 'ROOT_NODE_ID') {
                // Blocks the expanding feature on the second row
                return undefined;
              }

              return {
                component: 'agGroupCellRenderer',
              };
            },
          }}
          groupRowRendererParams={{
            suppressCount: true,
            suppressPadding: true,
          }}
          getRowStyle={(params) => {
            if (params.node.uiLevel === 0) {
              return {
                fontWeight: 'bold',
                backgroundColor: globalTheme.colors.aqua,
              };
            }
          }}
          groupDefaultExpanded={category?.length ? 1 : 0}
          suppressAggFuncInHeader={true}
          overlayLoadingTemplate={loadingTemplate}
          suppressHorizontalScroll={false}
          suppressExcelExport={true}
          suppressDragLeaveHidesColumns
        />
      </div>
    </div>
  );
};
